/* eslint-disable max-lines */
import {
  getCallTimeRangeNode,
  getTwilioLogDescriptionNode,
} from '@hakimo-ui/hakimo/feature-shared';
import {
  AlarmUpdate,
  AlarmUpdatesDTO,
  LocationAlarmUpdateType,
} from '@hakimo-ui/hakimo/types';
import {
  allocatedTimelineIcon,
  callMadeIcon,
  checkTimelineIcon,
  commentTimelineIcon,
  escalationOpenTimelineIcon,
  hakimoTimelineIcon,
  inProgressTimelineIcon,
  orphanTimelineIcon,
  pendingTimelineIcon,
  speakerTalkdownTimelineIcon,
  TimelineEvent,
  viewTimelineIcon,
} from '@hakimo-ui/hakimo/ui-elements';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { mapStatusToStatusType } from '../../util/common';
import {
  getTalkdownExtraDetailsModalContent,
  getTalkdownExtraDetailsNode,
} from './extraDetailsUtil';
import { UpdateObject } from './types';
dayjs.extend(utc);

const HAKIMO_USERNAME = 'Hakimo AI Engine';
const ADD_COMMENT_EVENT = 'Added a comment';
const ACS_ACKNOWLEDGMENT_EVENT = 'ACS Alarm Acknowledgment';
const SPEAKER_TALKDOWN_EVENT = 'Responded via speaker talk-down';

const AI_ENGINE_NAME = 'Hakimo AI Engine';
export function getAlarmUpdatesSearchParams(timestamp?: string): string {
  const params = new URLSearchParams();
  if (timestamp) {
    const date = dayjs.utc(timestamp);
    const partitionKey = date.format('YYYYMM');
    params.append('partitionKey', partitionKey);
  }
  return params.toString();
}

export function getTimelineIcon(update: UpdateObject): JSX.Element {
  if (update.user?.name === HAKIMO_USERNAME || update.user === null) {
    return hakimoTimelineIcon;
  }
  if (update.status) {
    switch (mapStatusToStatusType(update.status)) {
      case 'Resolved':
        return checkTimelineIcon;
      case 'In Progress':
        return inProgressTimelineIcon;
      case 'Pending':
        return pendingTimelineIcon;
    }
  }
  switch (update.event) {
    case SPEAKER_TALKDOWN_EVENT:
    case LocationAlarmUpdateType.MANUAL_TALKDOWN:
      return speakerTalkdownTimelineIcon;
    case LocationAlarmUpdateType.VIEW_ALARM:
      return viewTimelineIcon;
    case LocationAlarmUpdateType.CALL_SMS_LOGS:
      return callMadeIcon;
    case LocationAlarmUpdateType.ALLOCATION:
      return allocatedTimelineIcon;
    case LocationAlarmUpdateType.ORPHAN:
      return orphanTimelineIcon;
    case LocationAlarmUpdateType.ESCALATION_OPEN:
      return escalationOpenTimelineIcon;
    default:
      return commentTimelineIcon;
  }
}

export function getEventDescription(
  update: UpdateObject,
  assumeHakimo = false,
  locationAlarmId?: string
) {
  if (update.status) {
    let description = '';
    if (assumeHakimo) {
      description = `${update.user?.name ?? AI_ENGINE_NAME} changed status to ${
        update.status
      }`;
    } else if (update.user?.name !== undefined) {
      description = `${update.user?.name} changed status to ${update.status}`;
    } else {
      description = `Status changed to ${update.status}`;
    }
    if (update.comment) {
      description = description + `: ${update.comment}`;
    }
    return description;
  }
  if (
    update.event === ADD_COMMENT_EVENT ||
    update.event === LocationAlarmUpdateType.ADD_COMMENT
  ) {
    if (assumeHakimo) {
      return `${update.user?.name ?? AI_ENGINE_NAME}: "${update.comment}"`;
    } else if (update.user?.name !== undefined) {
      return `${update.user?.name}: "${update.comment}"`;
    }
    return `"${update.comment}"`;
  }
  if (update.event === ACS_ACKNOWLEDGMENT_EVENT) {
    return `Alarm Acknowledged via ACS: ${update.comment}`;
  }
  if (
    update.event === SPEAKER_TALKDOWN_EVENT ||
    update.event === LocationAlarmUpdateType.MANUAL_TALKDOWN
  ) {
    if (assumeHakimo) {
      return `${
        update.user?.name ?? AI_ENGINE_NAME
      } responded via speaker talk-down`;
    } else if (update.user?.name !== undefined) {
      return `${update.user?.name} responded via speaker talk-down`;
    }
    return `Speaker talk-down conducted`;
  }
  if (update.event === LocationAlarmUpdateType.VIEW_ALARM) {
    if (update.user?.name !== undefined) {
      return `${update.user?.name} viewed the alarm`;
    }
    return 'Alarm was viewed';
  }
  if (update.event === LocationAlarmUpdateType.CALL_SMS_LOGS) {
    return getTwilioLogDescriptionNode(update, { locationAlarmId });
  }
  if (update.event === LocationAlarmUpdateType.ALLOCATION) {
    return `Location Alarm allocated${
      update.user?.name ? ` to ${update.user.name}` : ''
    }`;
  }
  if (update.event === LocationAlarmUpdateType.ORPHAN) {
    return `Location Alarm unassigned`;
  }
  if (update.event === LocationAlarmUpdateType.ESCALATION_OPEN) {
    const name = update.user?.name ?? '';
    const comment = update.escalation_comment?.trim();
    return `Location alarm escalated${name ? ` by ${name}` : ''}${
      comment ? `\nEscalation Comment: ${comment}` : ''
    }`;
  }
  return `${update.user?.name} added feedback: ${update.comment}`;
}

export function getUpdateEvents(updates: AlarmUpdatesDTO): TimelineEvent[] {
  const getTime = (u: AlarmUpdate) =>
    new Date(u.timestamp ?? u.update_time ?? 0).getTime() || 0;
  const items = [...updates.items].sort((a, b) => getTime(b) - getTime(a));
  return items.map((update) => {
    const time = new Date(update.timestamp).toLocaleString();
    const timelineEvent: TimelineEvent = {
      icon: getTimelineIcon(update),
      description: getEventDescription(update),
      time,
      audio_url: update.audio_url,
    };
    if (
      update.event === LocationAlarmUpdateType.CALL_SMS_LOGS &&
      update.twilio_ssid
    ) {
      timelineEvent.timeNode = getCallTimeRangeNode(update.twilio_ssid, time);
    }
    if (update.event === SPEAKER_TALKDOWN_EVENT) {
      timelineEvent.extraDetails = getTalkdownExtraDetailsNode(update);
      timelineEvent.extraDetailsModalContent =
        getTalkdownExtraDetailsModalContent(update);
    }
    return timelineEvent;
  });
}
